import { useDorisData } from 'src/utils/Hooks/useDorisData';
import { TrafficSummaryResponse } from './types';

/**
 * Arguments for the market share data fetcher hook
 */
export interface UseDorisMarketShareDataFetcherArgs {
  queryKey: string;
  metricType: string;
  methodName: string;
  resultCount: number;
  showMarketShare: boolean;
  skipComparison: boolean;
  startWeekId: number;
  endWeekId: number;
  comparisonStartWeekId: number;
  comparisonEndWeekId: number;
  baseParams: Record<string, unknown>;
  comparisonParams?: Record<string, unknown> | null;
  marketShareRequestBuilder: (params: any, isComparison: boolean) => any;
}

/**
 * Return type for the market share data fetcher hook
 */
export interface UseDorisMarketShareDataFetcherResult {
  marketPrimaryData: TrafficSummaryResponse | undefined;
  marketComparisonData: TrafficSummaryResponse | undefined;
  isMarketPrimaryLoading: boolean;
  isMarketComparisonLoading: boolean;
}

/**
 * Hook that handles fetching market share data from Doris
 * 
 * This is only active when showMarketShare is true
 */
export const useDorisMarketShareDataFetcher = ({
  queryKey,
  metricType,
  methodName,
  resultCount,
  showMarketShare,
  skipComparison,
  startWeekId,
  endWeekId,
  comparisonStartWeekId,
  comparisonEndWeekId,
  baseParams,
  comparisonParams,
  marketShareRequestBuilder
}: UseDorisMarketShareDataFetcherArgs): UseDorisMarketShareDataFetcherResult => {
  // Market data query for the primary period
  const { data: marketPrimaryData, isLoading: isMarketPrimaryLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    resultCount,
    ...baseParams,
    queryKey: `market_${queryKey}_${metricType}_${startWeekId}_${endWeekId}`,
    customTimePeriods: {
      startWeekId,
      endWeekId
    },
    skipComparison: true,
    additionalParams: showMarketShare
      ? marketShareRequestBuilder(
          {
            ...baseParams,
            startWeekId,
            endWeekId
          },
          false
        ) || {}
      : {},
    enabled: showMarketShare,
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Market data query for the comparison period
  const { data: marketComparisonData, isLoading: isMarketComparisonLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    resultCount,
    queryKey: `market_comparison_${queryKey}_${metricType}_${comparisonStartWeekId}_${comparisonEndWeekId}`,
    trendIds: comparisonParams?.trendIds,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    skipComparison: true,
    additionalParams:
      showMarketShare && !skipComparison
        ? marketShareRequestBuilder(
            {
              ...baseParams,
              startWeekId: comparisonStartWeekId,
              endWeekId: comparisonEndWeekId
            },
            true
          ) || {}
        : {},
    enabled: showMarketShare && !skipComparison,
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  return {
    marketPrimaryData,
    marketComparisonData,
    isMarketPrimaryLoading,
    isMarketComparisonLoading
  };
};
